/** Где запущено приложение и как оно доказывает серверу, кто перед ним.
 *
 *  - Telegram Mini App: личность подтверждает подпись Telegram (initData) —
 *    токен не нужен, экрана входа нет.
 *  - Нативное приложение (Capacitor) и обычный браузер: человек входит через
 *    Telegram или Apple и получает токен сессии; он лежит в хранилище устройства.
 */

import { tg } from './telegram'

interface CapacitorGlobal {
  isNativePlatform?: () => boolean
  getPlatform?: () => string
}

const cap = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor

export const isTelegramApp = Boolean(tg?.initData)
export const isNative = Boolean(cap?.isNativePlatform?.())
export const nativePlatform: string = isNative ? (cap?.getPlatform?.() ?? 'ios') : 'web'
export const isIOSApp = isNative && nativePlatform === 'ios'
export const isAndroidApp = isNative && nativePlatform === 'android'

/** Системная кнопка «Назад» Android: закрывает открытый экран (то же, что
 *  полоска «‹ Назад»), а на главном экране сворачивает приложение — как
 *  принято на Android, без выхода и потери входа. */
let androidHome: (() => void) | null = null

/** Вкладка «Создать» или «Профиль»: «Назад» ведёт на ленту, а не сворачивает. */
export function setAndroidHome(handler: (() => void) | null): void {
  androidHome = handler
}

export function setupAndroidBack(currentBack: () => (() => void) | null): void {
  if (!isAndroidApp) return
  void import('@capacitor/app').then(({ App }) => {
    void App.addListener('backButton', () => {
      const back = currentBack()
      if (back) back()
      else if (androidHome) androidHome()
      else void App.minimizeApp()
    })
  })
}

const KEY = 'por4.session'

let cached: string | null | undefined

export function getToken(): string | null {
  if (cached === undefined) {
    try {
      cached = window.localStorage.getItem(KEY)
    } catch {
      cached = null
    }
  }
  return cached
}

export function setToken(token: string | null): void {
  cached = token
  try {
    if (token) window.localStorage.setItem(KEY, token)
    else window.localStorage.removeItem(KEY)
  } catch {
    /* хранилище недоступно — токен проживёт до закрытия приложения */
  }
}

/** Нужен ли экран входа: не Telegram и токена нет. В режиме разработки без
 *  токена работает заголовок X-Dev-User — экран входа показываем только по ?login=1. */
export function needsLogin(): boolean {
  if (isTelegramApp || getToken()) return false
  if (import.meta.env.DEV) return new URLSearchParams(window.location.search).has('login')
  return true
}

/** Открыть ссылку вне приложения. tg:// и https://t.me внутри нативной
 *  оболочки сами не откроются — нужен системный вызов. */
export async function openOutside(url: string, fallback?: string): Promise<void> {
  if (isNative) {
    // Не установлен Telegram — openUrl не падает, а отвечает completed: false;
    // тогда открываем запасную ссылку (https://t.me — откроет браузер)
    const { AppLauncher } = await import('@capacitor/app-launcher')
    for (const target of fallback ? [url, fallback] : [url]) {
      try {
        const { completed } = await AppLauncher.openUrl({ url: target })
        if (completed) return
      } catch {
        /* пробуем следующую ссылку */
      }
    }
  }
  window.open(fallback ?? url, '_blank', 'noopener')
}

/** Sign in with Apple — только в приложении для iOS. */
export async function appleSignIn(): Promise<{ identityToken: string; firstName: string | null }> {
  const { SignInWithApple } = await import('@capacitor-community/apple-sign-in')
  const result = await SignInWithApple.authorize({
    clientId: 'app.porcuatro',
    redirectURI: 'https://porcuatro.app',
    scopes: 'name',
  })
  return {
    identityToken: result.response.identityToken,
    firstName: result.response.givenName ?? null,
  }
}

/** Адрес страницы сайта (условия, политика, поддержка). В нативной оболочке
 *  своего домена нет — берём боевой. */
export function siteUrl(path: string): string {
  const origin = isNative || import.meta.env.DEV ? 'https://porcuatro.app' : window.location.origin
  return `${origin}${path}`
}
